import React, { createContext, useState, useEffect, useContext } from 'react';
import { api } from '../services/api';
import { translations } from '../i18n/translations';
import { da, enGB } from 'date-fns/locale';

const AppContext = createContext();

const DEFAULT_SETTINGS = {
  company: {
    name: 'BakeryPro Artisan',
    logo: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=128&h=128&fit=crop&auto=format&q=80',
    address: '123 Baker Street',
    location: 'Copenhagen 2100',
    phone: '+45 12 34 56 78',
    email: 'hello@bakerypro.com',
    website: 'www.bakerypro.com',
    contactPerson: 'Master Baker',
    notes: ''
  },
  print: {
    showLogo: true,
    showAddress: true,
    footerText: 'Thank you for choosing artisan quality.'
  },
  system: {
    gasUrl: import.meta.env.VITE_GAS_URL || '',
    spreadsheetId: '',
    settingsPin: '1234',
    language: 'da'
  }
};

export const AppProvider = ({ children }) => {
  const [data, setData] = useState({
    products: [],
    recipes: [],
    customers: [],
    customerProducts: [],
    orders: [],
    orderLines: []
  });

  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('bakery_settings');
    if (!saved) return DEFAULT_SETTINGS;
    try {
      const parsed = JSON.parse(saved);
      return {
        company: { ...DEFAULT_SETTINGS.company, ...(parsed.company || {}) },
        print: { ...DEFAULT_SETTINGS.print, ...(parsed.print || {}) },
        system: { ...DEFAULT_SETTINGS.system, ...(parsed.system || {}) }
      };
    } catch (e) {
      return DEFAULT_SETTINGS;
    }
  });

  const [loading, setLoading] = useState(true);

  // Translation Helper
  const t = (path) => {
    const lang = settings.system.language || 'da';
    const keys = path.split('.');
    let value = translations[lang] || translations['en'];
    
    for (const key of keys) {
      value = value?.[key];
    }
    
    // Fallback to English if key missing
    if (value === undefined && lang !== 'en') {
      let fallbackValue = translations['en'];
      for (const key of keys) {
        fallbackValue = fallbackValue?.[key];
      }
      return fallbackValue || path;
    }
    
    return value || path;
  };

  // Unit Formatting Helper
  const formatUnit = (unitValue) => {
    if (!unitValue) return '';
    const cleanValue = String(unitValue).toLowerCase().trim();
    const translated = t(`units.${cleanValue}`);
    // If t returns the path back, it's not a known system unit, so return original
    return translated.includes('units.') ? unitValue : translated;
  };

  // Locale Helper for date-fns
  const getDateLocale = () => {
    return settings.system.language === 'da' ? da : enGB;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [products, recipes, customers, cp, orders, ol] = await Promise.all([
        api.getProducts(), api.getRecipes(), api.getCustomers(), api.getCustomerProducts(), api.getOrders(), api.getOrderLines()
      ]);
      setData({
        products: products || [],
        recipes: recipes || [],
        customers: customers || [],
        customerProducts: cp || [],
        orders: orders || [],
        orderLines: ol || []
      });
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('bakery_settings', JSON.stringify(newSettings));
  };

  return (
    <AppContext.Provider value={{ data, loading, settings, updateSettings, refreshData: loadData, t, getDateLocale, formatUnit }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);